import { createVeriffFrame, MESSAGES } from '@veriff/incontext-sdk'

import {
    useCallback,
    useEffect,
} from 'react'

import { useMutation } from '@tanstack/react-query'

import { IdCard, CheckCircle } from 'lucide-react'

import startPreCheckSessionMutation from './services/start-pre-check-session'
import Overlay from '../../../../../../Components/Overlay'

// Module-level guards. These deliberately live OUTSIDE the component instance
// so they survive remounts (the parent renders <PreCheck key={documentId} />,
// so React tears the instance down and back up when the document changes) and
// any effect double-invocation. Component-instance refs reset to null on
// remount, which let createVeriffFrame fire a second time against the same
// sessionUrl in production — Veriff then treats the session as opened on
// another device and skips the QR screen.
const startedSessionKeys = new Set()   // `${formKey}:${documentId}` already started
let activeVeriffUrl = null             // sessionUrl the current frame was built for
let activeVeriffFrame = null           // the live Veriff frame, if any

const VerificationComponent = ({
    data,
    onSubmitted,
    documentText,
    handleNext,
    formKey,
    refetch,
    documentId,
}) => {

    const {
        isPending: startPreCheckSessionIsPending,
        mutate: startPreCheckSession,
    } = useMutation({
        mutationFn: startPreCheckSessionMutation,
        onSuccess: async () => {
            await refetch()
        },
    })

    useEffect(() => {
        if (data?.sessionUrl) return
        if (data?.status === 'submitted') return
        // Guard keyed by the session identity (not the instance) so a remount
        // mid-start can never kick off a second session.
        const startKey = `${formKey}:${documentId}`
        if (startedSessionKeys.has(startKey)) return
        startedSessionKeys.add(startKey)
        startPreCheckSession({
            key: formKey,
            currentPageType: "pre_check",
            currentPageDocumentId: documentId,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleRedirect = useCallback(() => {
        if (onSubmitted) {
            onSubmitted()
        }
    }, [onSubmitted])

    useEffect(() => {
        if (!data?.sessionUrl) {
            return
        }
        if (data?.status === "submitted") {
            return
        }

        const url = data.sessionUrl

        // Only ever build one frame per sessionUrl, across the whole page
        // lifetime. A second createVeriffFrame against the same sessionUrl
        // makes Veriff treat it as opened on another device and skips the QR
        // screen. This must outlive the component instance because the
        // key={documentId} remount would otherwise reset an instance ref.
        if (activeVeriffUrl === url) {
            return
        }

        // A different session became active (e.g. moving to another document) —
        // tear down the previous frame before building the new one.
        if (activeVeriffFrame) {
            activeVeriffFrame.close()
            activeVeriffFrame = null
        }

        activeVeriffUrl = url
        activeVeriffFrame = createVeriffFrame({
            url: data.sessionUrl,
            embedded: true,
            embeddedOptions: {
                rootElementID: 'embedded-container',
            },
            onEvent: function (msg) {
                switch (msg) {
                    case MESSAGES.STARTED:
                        console.log('Verification started');
                        break;
                    case MESSAGES.SUBMITTED: {
                        handleRedirect()
                        break;
                    }
                    case MESSAGES.FINISHED:
                        console.log('Verification finished');
                        break;
                    case MESSAGES.CANCELED:
                        console.log('Verification closed');
                        break;
                    case MESSAGES.RELOAD_REQUEST:
                        console.log('Verification reloaded');
                        break;
                }
            },
        })

        // No cleanup that closes the frame here. Closing on unmount would let
        // the very next mount (from the key={documentId} remount) rebuild a
        // frame for the same sessionUrl — the exact duplicate we are guarding
        // against. The frame is torn down above only when a genuinely new
        // sessionUrl takes over.
        //
        // Intentionally keyed on sessionUrl only. Re-running on `status`
        // changes would inject a duplicate frame for the same session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.sessionUrl])

    const isAlreadySubmitted = data?.status === 'submitted'

    return (
        <div className="flex items-center justify-center px-4 py-10">
            <div className="max-w-3xl w-full bg-white shadow-xl rounded-2xl p-8 space-y-6">
                <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-gray-800">Identity Verification</h1>
                    {documentText && (
                        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 text-sm font-medium px-3 py-1.5 rounded-lg">
                            <IdCard className="w-4 h-4 shrink-0" />
                            {documentText}
                        </div>
                    )}
                    <p className="text-gray-600 text-base">
                        Please complete the identity verification steps below.
                    </p>
                </div>

                {isAlreadySubmitted ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-5 text-center">
                        <CheckCircle className="w-16 h-16 text-green-500" />
                        <div className="space-y-1">
                            <h2 className="text-xl font-semibold text-gray-800">Verification Already Submitted</h2>
                            <p className="text-gray-500 text-sm max-w-sm">
                                Your identity verification has already been submitted.
                            </p>
                            <div className="text-center">
                                <button
                                    onClick={handleNext}
                                    className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-md transition-all disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="w-full md:w-[635px] h-auto md:h-[670px] min-h-[670px]" id="embedded-container" />
                )}
            </div>

            {startPreCheckSessionIsPending && <Overlay />}
        </div>
    )

}

export default VerificationComponent
